import React from 'react';
import { TextLink, CTAButton, Notification } from '@ovotech/element-v4';
import { PhoneLink } from '@/src/components/PhoneLink';
import styles from './EligibilityNotification.module.css';

export enum NotificationId {
  HOME_CAN_BE_POWERED_BY_OVO = 'HOME_CAN_BE_POWERED_BY_OVO',
  HOME_ALREADY_POWERED_BY_OVO = 'HOME_ALREADY_POWERED_BY_OVO',
  HOME_HAS_COMPLEX_METER_SETUP = 'HOME_HAS_COMPLEX_METER_SETUP',
  COT_TOO_FAR_IN_FUTURE = 'COT_TOO_FAR_IN_FUTURE',
  ERROR = 'ERROR',
}

type EligibilityNotificationProps = {
  id: NotificationId;
};

export const EligibilityNotification = ({
  id,
}: EligibilityNotificationProps) => {
  if (id === NotificationId.HOME_CAN_BE_POWERED_BY_OVO) {
    return (
      <Notification
        id={id}
        title="We can power your new home"
        variant="success"
      >
        Keep your account in a few simple steps. Keep all your perks like Power
        Move, Beyond, banked EV miles, and more. We'll help you save energy and
        money, as we cut our collective carbon footprint together. Select ‘Get a
        quote’ to see what tariffs are available for you.
      </Notification>
    );
  }

  if (id === NotificationId.HOME_ALREADY_POWERED_BY_OVO) {
    return (
      <PoweredByOvoNotification id={id}>
        If you just moved in, or are moving in soon and don't have an OVO
        account for this address yet, please continue and we'll set one up for
        you.
      </PoweredByOvoNotification>
    );
  }

  if (id === NotificationId.HOME_HAS_COMPLEX_METER_SETUP) {
    return (
      <>
        <Notification
          className={styles.desktopOnly}
          id={id}
          title="Call us about supplying this address"
          variant="error"
        >
          It looks like there's a complex meter setup at this property. If you'd
          like OVO to power your new home, please call us on{' '}
          <PhoneLink className={styles.phone} number="0330 303 5063" /> from 9am
          to 5pm, Monday to Friday, except on bank holidays, to discuss the
          tariffs we have available.
        </Notification>
        <Notification
          className={styles.mobileOnly}
          id={id}
          title="Call us about supplying this address"
          variant="error"
        >
          It looks like there's a complex meter setup at this property. If you'd
          like OVO to power your new home, please call us (9am to 5pm, Monday to
          Friday) to discuss the tariffs we have available.
        </Notification>
        <TextLink className={styles.mobileOnly} href="tel:03303035063">
          <CTAButton fullWidth>Call OVO</CTAButton>
        </TextLink>
      </>
    );
  }

  if (id === NotificationId.ERROR) {
    return (
      <>
        <Notification
          id={id}
          title="We're sorry, something went wrong"
          variant="error"
        >
          Please try again later - or call us on{' '}
          <PhoneLink className={styles.phone} number="0330 303 5063" /> from 9am
          to 5pm, Monday to Friday, except on bank holidays.
        </Notification>
        <TextLink className={styles.mobileOnly} href="tel:03303035063">
          <CTAButton fullWidth>Call OVO</CTAButton>
        </TextLink>
      </>
    );
  }

  if (id === NotificationId.COT_TOO_FAR_IN_FUTURE) {
    return (
      <PoweredByOvoNotification id={id}>
        Good news! We already supply your new address. However, since your move
        in date is over 7 days away, we can't get you set up there yet. Please
        come back closer to the time and we can get a quote for you.
      </PoweredByOvoNotification>
    );
  }

  return null;
};

function PoweredByOvoNotification({
  id,
  children,
}: {
  id: NotificationId;
  children: React.ReactNode;
}) {
  return (
    <Notification id={id} title="This home is powered by OVO" variant="success">
      {children}
    </Notification>
  );
}
